// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { createContext, useCallback, useContext } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import type { ReactNode } from 'react';
import { useAuthConfig } from '../hooks/useAuthConfig';

type GetCaptchaToken = (action: string) => Promise<string | undefined>;

const CaptchaContext = createContext<GetCaptchaToken>(async () => undefined);

/**
 * Hook to get a reCAPTCHA token for a given action.
 * Returns undefined when reCAPTCHA is disabled.
 * Must be used inside a RecaptchaGuard.
 */
export function useCaptchaToken(): GetCaptchaToken {
  return useContext(CaptchaContext);
}

/** Inner provider that uses useGoogleReCaptcha (requires GoogleReCaptchaProvider). */
function CaptchaContextProvider({ children }: { children: ReactNode }) {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const getToken: GetCaptchaToken = useCallback(
    async (action: string) => {
      if (!executeRecaptcha) return undefined;
      return executeRecaptcha(action);
    },
    [executeRecaptcha],
  );

  return <CaptchaContext.Provider value={getToken}>{children}</CaptchaContext.Provider>;
}

interface RecaptchaGuardProps {
  children: ReactNode;
}

/**
 * Conditionally wraps children with GoogleReCaptchaProvider when reCAPTCHA
 * is enabled in the backend config. Provides a useCaptchaToken() hook for
 * forms to get reCAPTCHA tokens before submission.
 *
 * When disabled, useCaptchaToken() returns a no-op that resolves to undefined.
 */
export function RecaptchaGuard({ children }: RecaptchaGuardProps) {
  const { recaptchaEnabled, recaptchaSiteKey, isLoading } = useAuthConfig();

  if (isLoading || !recaptchaEnabled || !recaptchaSiteKey) {
    return <>{children}</>;
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
      <CaptchaContextProvider>{children}</CaptchaContextProvider>
    </GoogleReCaptchaProvider>
  );
}
